
import { Vue, Options } from 'vue-class-component'
import SuggestBox from './SuggestBox.vue'
import SvgIcon from './SvgIcon.vue'
import EngineBox from './EngineBox.vue'

import { engines, findEngineByShortCutkey } from '@/utils/search'
import { gotoPage, smartGotoPage } from '@/utils/public'

@Options({
  components: {
    SuggestBox,
    EngineBox,
    SvgIcon
  },
  props: {
    searchInputFocused: Boolean
  }
})
export default class SearchBox extends Vue {
  suggests = new Array<string>()
  selected = -1
  searchInputFocused!: boolean
  hasSuggestions = false
  showEngines = false
  enginesData = {
    currentEngineIndex: 0
  }

  get currentEngineIndex () {
    return this.enginesData.currentEngineIndex
  }

  set currentEngineIndex (index: number) {
    this.enginesData.currentEngineIndex = index
    this.$emit('engineChanged', this.engineLogoName())
  }

  engineLogoName () {
    return engines[this.enginesData.currentEngineIndex].logo
  }

  get currentEngine () {
    return engines[this.enginesData.currentEngineIndex]
  }

  private loadEngineData () {
    const tmp = localStorage.getItem('enginesData')
    if (tmp) {
      this.enginesData = JSON.parse(tmp)
    }
    this.$emit('engineChanged', this.engineLogoName())
  }

  setEngineIndex (index: number) {
    this.currentEngineIndex = index
    const input = document.getElementById('search-input') as HTMLInputElement | null
    if (input) this.getSuggests(input)
    localStorage.setItem('enginesData', JSON.stringify(this.enginesData))
    this.showEngines = false
  }

  switchEngine (sentence: string) {
    if (sentence.length < 1) return false
    const letter = sentence[sentence.length - 1]
    if (!/[a-zA-Z]/.test(letter)) return false
    const index = findEngineByShortCutkey(letter.toLowerCase())
    if (this.currentEngineIndex === index) return false
    if (index !== -1) {
      this.currentEngineIndex = index
      localStorage.setItem('enginesData', JSON.stringify(this.enginesData))
      return true
    }
    return false
  }

  shiftEngine (forward: boolean) {
    if (forward) {
      if (this.currentEngineIndex + 1 >= engines.length) {
        this.currentEngineIndex = 0
      } else {
        ++this.currentEngineIndex
      }
    } else {
      if (this.currentEngineIndex === 0) {
        this.currentEngineIndex = engines.length - 1
      } else {
        --this.currentEngineIndex
      }
    }
    localStorage.setItem('enginesData', JSON.stringify(this.enginesData))
  }

  clearInput () {
    const input = document.getElementById('search-input') as HTMLInputElement | null
    if (!input) return

    input.value = ''
    input.focus()
  }

  mounted (): void {
    this.loadEngineData()

    document.body.addEventListener('click', ev => {
      if (!ev.target) return
      const target = ev.target as HTMLElement
      if (target.id !== 'search-input') {
        if (target.classList.contains('suggest-item')) {
          smartGotoPage(target.innerHTML, this.currentEngine.getUrl)
        }
        this.hasSuggestions = false
      } else {
        const target = ev.target as HTMLInputElement
        this.hasSuggestions = target.value.length !== 0
        this.toggleFocus(true)
        return
      }
      this.toggleFocus(false)
    })

    document.addEventListener('keydown', ev => {
      if (ev.key === 'Tab') {
        // check the target element or its parent element is mask
        ev.preventDefault()

        let target = ev.target as HTMLElement
        while (target) {
          if (target.classList.contains('mask')) {
            return
          }
          target = target.parentElement as HTMLElement
        }

        if (!this.searchInputFocused) {
          const input = document.getElementById('search-input') as HTMLInputElement | null
          if (input && document.activeElement !== input) {
            input.focus()
            this.toggleFocus(true)
          }
        }
      }
    })
  }

  directSearch () {
    const el = document.getElementById('search-input') as HTMLInputElement | null
    if (el) gotoPage(el.value, this.currentEngine.getUrl)
  }

  toggleFocus (on: boolean) {
    if (!on) {
      this.selected = -1
    }
    if (on === this.searchInputFocused) return
    this.$emit('searchInputFocus', on)
  }

  submitSearch (event: KeyboardEvent) {
    if (!event.target) return
    const target = event.target as HTMLInputElement
    if (event.key === 'Tab') {
      if (!event.shiftKey && this.switchEngine(target.value)) {
        target.value = target.value.slice(0, -1)
      } else {
        this.shiftEngine(!event.shiftKey)
        this.getSuggests(target)
      }
      event.preventDefault()
    }
    if (target.value.length === 0) {
      return
    }
    switch (event.key) {
      case 'Enter':
        if (this.selected !== -1 && this.selected < this.suggests.length) {
          smartGotoPage(this.suggests[this.selected], this.currentEngine.getUrl)
        } else {
          gotoPage(target.value, this.currentEngine.getUrl)
        }
        break
      case 'ArrowUp':
        if (this.suggests.length > 0) {
          if (this.selected - 1 < 0) {
            this.selected = this.suggests.length - 1
          } else {
            --this.selected
          }
          target.value = this.suggests[this.selected]
          target.setSelectionRange(0, target.value.length)
          event.preventDefault()
        }
        break
      case 'ArrowDown':
        if (this.suggests.length > 0) {
          if (this.selected + 1 >= this.suggests.length) {
            this.selected = 0
          } else {
            ++this.selected
          }
          target.value = this.suggests[this.selected]
          target.setSelectionRange(0, target.value.length)
          event.preventDefault()
        }
        break
      default:
        // console.log(event.key)
        break
    }
  }

  getSuggests (target: HTMLInputElement) {
    if (!target || target.value.length === 0) {
      this.hasSuggestions = false
      return
    }

    this.currentEngine.getSuggests(target.value, (result: string[]) => {
      this.suggests = result
      this.selected = -1
      if (this.suggests.length) {
        this.hasSuggestions = target.value.length !== 0
      }
    })
  }
}
